import React from "react";
import { motion } from "framer-motion";
import { useMicrospecThemeLanguageContext } from "../../../MicrospecDynamicUI/Themes";
import { Box, Typography, Container } from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";

const FooterSection = () => {
  const { localizationProvider, dir } = useMicrospecThemeLanguageContext();

  const brand = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.footer.brand",
  });
  const description = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.footer.description",
  });
  const rights = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.footer.rights",
  });
  const contact = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.footer.contact",
  });
  const privacy = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.footer.privacy",
  });
  const terms = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.footer.terms",
  });

  const navFeatures = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.nav.features",
  });
  const navHowItWorks = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.nav.howItWorks",
  });

  return (
    <Box
      component="footer"
      className="bg-gradient-to-b from-[#0A0F2E] to-[#050816] pt-20 pb-8 mt-auto relative overflow-hidden"
    >
      {/* Luminous top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8B5CF6]/30 to-transparent" style={{ boxShadow: '0 0 20px rgba(139,92,246,0.15)' }} />
      <div className="absolute top-[-20%] left-[-5%] w-[30%] h-[50%] rounded-full bg-[#7393B3]/8 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-5%] w-[25%] h-[40%] rounded-full bg-[#8B5CF6]/6 blur-[80px] pointer-events-none" />

      <Container maxWidth="xl" dir={dir}>
        <div className="px-4 md:px-8">
          {/* Main footer content */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-14 border-b border-white/5">
            {/* Brand column - wider */}
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-5">
                <div className="relative">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#7393B3] via-[#8B5CF6] to-[#C084FC] flex items-center justify-center shadow-lg shadow-[#8B5CF6]/20">
                    <PsychologyIcon sx={{ color: "white", fontSize: 24 }} />
                  </div>
                  <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-[#7393B3]/15 to-[#8B5CF6]/15 blur-sm -z-10" />
                </div>
                <Typography className="font-bold text-white text-lg tracking-tight">
                  {brand}
                </Typography>
              </div>
              <Typography className="text-slate-400 text-sm leading-relaxed max-w-sm mb-6">
                {description}
              </Typography>

              {/* Mini brainwave decoration */}
              <svg
                viewBox="0 0 200 30"
                className="w-40 opacity-20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <motion.path
                  d="M0,15 Q20,15 30,8 T50,22 T70,10 T90,20 T110,12 T130,18 T150,15 T170,10 T200,15"
                  fill="none"
                  stroke="url(#footerWaveGrad)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2 }}
                />
                <defs>
                  <linearGradient
                    id="footerWaveGrad"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="#7393B3" />
                    <stop offset="100%" stopColor="#8B5CF6" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            {/* Navigation links column */}
            <div className="md:col-span-3">
              <Typography className="text-white/60 text-xs font-semibold uppercase tracking-[0.15em] mb-5">
                Navigate
              </Typography>
              <div className="flex flex-col gap-3">
                <a
                  href="#features"
                  className="text-slate-400 hover:text-white text-sm transition-colors no-underline hover:translate-x-1 transform duration-200 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 rounded-full bg-[#8B5CF6] opacity-0 group-hover:opacity-100 transition-opacity" />
                  {navFeatures}
                </a>
                <a
                  href="#how-it-works"
                  className="text-slate-400 hover:text-white text-sm transition-colors no-underline hover:translate-x-1 transform duration-200 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 rounded-full bg-[#8B5CF6] opacity-0 group-hover:opacity-100 transition-opacity" />
                  {navHowItWorks}
                </a>
                <a
                  href="#contact"
                  className="text-slate-400 hover:text-white text-sm transition-colors no-underline hover:translate-x-1 transform duration-200 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 rounded-full bg-[#8B5CF6] opacity-0 group-hover:opacity-100 transition-opacity" />
                  {contact}
                </a>
              </div>
            </div>

            {/* Legal links column */}
            <div className="md:col-span-3">
              <Typography className="text-white/60 text-xs font-semibold uppercase tracking-[0.15em] mb-5">
                Legal
              </Typography>
              <div className="flex flex-col gap-3">
                <a
                  href="#"
                  className="text-slate-400 hover:text-white text-sm transition-colors no-underline hover:translate-x-1 transform duration-200 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 rounded-full bg-[#C084FC] opacity-0 group-hover:opacity-100 transition-opacity" />
                  {privacy}
                </a>
                <a
                  href="#"
                  className="text-slate-400 hover:text-white text-sm transition-colors no-underline hover:translate-x-1 transform duration-200 inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 rounded-full bg-[#C084FC] opacity-0 group-hover:opacity-100 transition-opacity" />
                  {terms}
                </a>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <Typography className="text-slate-600 text-xs">
              &copy; {new Date().getFullYear()} {rights}
            </Typography>
            <div className="flex items-center gap-1.5">
              {["#7393B3", "#8B5CF6", "#C084FC"].map((color, i) => (
                <motion.div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: color }}
                  animate={{ opacity: [0.3, 0.8, 0.3] }}
                  transition={{ duration: 2, repeat: Infinity, delay: i * 0.4 }}
                />
              ))}
            </div>
          </div>
        </div>
      </Container>
    </Box>
  );
};

export default FooterSection;
